import { meterClass, num } from "../format";
import type { Stats, Subscription } from "../types";
import { Sparkline } from "./Sparkline";

export function TodayCard({ stats }: { stats: Stats | null }) {
  return (
    <div className="card">
      <h2>Today</h2>
      <div className="grid2">
        <div className="stat">
          <div className="k">Messages</div>
          <div className="v">{num(stats?.today)}</div>
        </div>
        <div className="stat">
          <div className="k">Characters</div>
          <div className="v">{num(stats?.today_chars)}</div>
        </div>
        <div className="stat">
          <div className="k">Avg latency</div>
          <div className="v">
            {stats?.avg_latency_ms ? `${stats.avg_latency_ms}ms` : "–"}
          </div>
        </div>
        <div className="stat">
          <div className="k">Failed</div>
          <div className="v">{num(stats?.failed)}</div>
        </div>
      </div>
    </div>
  );
}

function Rows({ pairs }: { pairs: [string, string][] }) {
  if (!pairs.length) return <span className="muted">nothing yet</span>;
  return (
    <>
      {pairs.map(([left, right]) => (
        <div key={left}>
          <span>{left}</span>
          <span className="n">{right}</span>
        </div>
      ))}
    </>
  );
}

/* Nobody budgets a stream in characters.

   "88,000 remaining" is a true number that answers no question anybody has. The
   two they do have are "how many more messages is that" and "at this rate, do I
   make it to the reset" — both of which the panel can work out from what the
   tenant has actually been spending. */
function forecast(
  subscription: Subscription,
  stats: Stats | null,
): { messages: number | null; daysLeft: number | null } {
  const avgChars =
    stats && stats.total > 0 ? stats.total_chars / stats.total : 0;
  const messages =
    avgChars > 0 && subscription.limit > 0
      ? Math.floor(subscription.remaining / avgChars)
      : null;

  const daily = stats?.daily_chars ?? [];
  // Yesterday and back, ignoring today: a day two hours old would read as a
  // collapse in spend and forecast a month of headroom that is not there.
  const settled = daily.slice(0, -1).filter((value) => value > 0);
  const perDay = settled.length
    ? settled.reduce((sum, value) => sum + value, 0) / settled.length
    : 0;
  const daysLeft =
    perDay > 0 && subscription.limit > 0
      ? Math.floor(subscription.remaining / perDay)
      : null;

  return { messages, daysLeft };
}

export function CreditsCard({
  subscription,
  stats,
}: {
  subscription: Subscription | null;
  stats: Stats | null;
}) {
  const pct = subscription?.limit ? (subscription.used / subscription.limit) * 100 : 0;
  const pairs: [string, string][] = subscription
    ? [
        ["tier", subscription.tier || "–"],
        ["used", `${num(subscription.used)} / ${num(subscription.limit)}`],
        ["remaining", num(subscription.remaining)],
        [
          "resets",
          subscription.resets_at
            ? new Date(subscription.resets_at * 1000).toLocaleDateString()
            : "–",
        ],
      ]
    : [];

  const outlook = subscription ? forecast(subscription, stats) : null;
  const daily = stats?.daily_chars ?? [];

  return (
    <div className="card">
      <h2>Credits</h2>
      <div className="rows">
        <Rows pairs={pairs} />
      </div>
      <div className={meterClass(pct, 75, 90)}>
        <div style={{ width: `${Math.min(100, pct)}%` }} />
      </div>

      {outlook?.messages !== null && outlook !== null && (
        <div className="forecast">
          <b>≈ {num(outlook.messages)}</b> more messages at your average length
          {outlook.daysLeft !== null && (
            <>
              {" · "}
              <b>{num(outlook.daysLeft)}</b> day{outlook.daysLeft === 1 ? "" : "s"} at
              your recent rate
            </>
          )}
        </div>
      )}

      {daily.some((value) => value > 0) && (
        <div className="burn">
          <div className="field-label">Characters per day</div>
          <Sparkline values={daily} label="Characters per day" />
        </div>
      )}
    </div>
  );
}
